import { type IWEIAsset, isIWEIAsset, } from "@bracket/blocksmith/types";
import Decimal from 'decimal.js';
import { getAsset, isKnownAsset, type AllChainImports, } from "./assets";
import type { WEIExponent, TokenSymbol, KnownChainId, } from "@bracket/blocksmith/types";

/**
 * WEI - A high-precision numeric class for blockchain applications
 *
 * Stores numeric values with configurable decimal points of precision using BigInt.
 * The internal bigint value represents the number multiplied by 10^precision.
 * For example, the decimal value 2.5 is stored internally as 2500000000000000000n (with 18 decimals).
 *
 * This is similar to how Ethereum stores values in Wei (10^18 wei = 1 ETH).
 * Commonly used in blockchain applications where JavaScript's Number primitive
 * lacks sufficient precision for token amounts, wei values, etc.
 */

/**
 * Default precision for WEI (18 decimal places)
 */
const DEFAULT_PRECISION = 18;

/**
 * High-precision numeric class for blockchain values
 *
 * @example
 * const amount = new WEI(2.5);        // Internally: 2500000000000000000n (18 decimals)
 * const usdc = new WEI(100, 6);       // USDC with 6 decimals
 * const withAsset = new WEI(1, { decimals: 8 }); // Bitcoin with 8 decimals
 * const doubled = amount.multiply(2);         // Result: 5.0
 */
export class WEI {
	private readonly value: bigint;
	private readonly precision: number;
	private readonly precisionMultiplier: bigint;
	public readonly asset: IWEIAsset | null;

	/**
	 * Creates a new WEI instance
	 *
	 * @param {bigint | number | string} input - The initial value
	 *   - bigint: Raw scaled value (already multiplied by 10^precision)
	 *   - number: Decimal value that will be scaled (e.g., 2.5)
	 *   - string: Raw scaled value as string (must be integer, no decimals) (e.g., "2500000")
	 * @param {number | IWEIAsset} config - Optional precision (number) or asset config (object with decimals)
	 * @throws {Error} If the input cannot be converted to a valid WEI or if string contains decimals
	 *
	 * @example
	 * new WEI(2.5)                              // Decimal number: 2.5 with 18 decimals
	 * new WEI("1500000", 6)                     // Raw WEI string: represents 1.5 with 6 decimals
	 * new WEI(100, { decimals: 8 })             // Decimal number: 100 with 8 decimals
	 * new WEI(2000000000000000000n)             // Raw bigint with 18 decimals
	 * new WEI("2.5")                            // ERROR: strings must be raw WEI values (no decimals)
	 */
	constructor(input: bigint | number | string, config?: number | IWEIAsset | [WEIExponent, TokenSymbol, KnownChainId]) {
		// Determine precision and asset
		if (config === undefined) {
			this.precision = DEFAULT_PRECISION;
			this.asset = null;
		}
		else if (typeof config === "number") {
			if (!Number.isInteger(config) || config < 0) {
				throw new Error(`Invalid precision: ${config}. Must be a non-negative integer.`);
			}
			this.precision = config;
			this.asset = null;
		}
		else if (isIWEIAsset(config)) {
			this.precision = config.decimals;
			this.asset = config;
		}
		else if( Array.isArray(config) && config.length === 3 ) {

			const [ decimals, symbol, chainId, ] = config as [number, AllChainImports, KnownChainId];
			if(isKnownAsset(symbol as AllChainImports, chainId || 1)) {
				const asset = getAsset(symbol, chainId||1);
				if(asset) {
					this.precision = asset.decimals;
					this.asset = asset;
				}
				console.warn(`asset "${symbol}" not found on chain ${chainId}, using non-asset precision ${decimals}`);
			}
			this.precision = decimals;
			this.asset = null;
		}
		else {

			throw new Error(`Invalid config type. Expected number or IWEIAsset, got ${typeof config}`);
		}

		this.precisionMultiplier = 10n ** BigInt(this.precision);

		// Parse input based on type
		if (typeof input === "bigint") {
			// Raw bigint value is stored as-is (assumed to be already scaled)
			this.value = input;
		} else if (typeof input === "string") {
			// String must be raw WEI value (integer string, no decimals)
			this.value = WEI.parseWEIString(input.length ? input : "0");
		} else if (typeof input === "number") {
			if (!Number.isFinite(input)) {
				throw new Error(`Invalid number input for WEI: ${input}`);
			}
			// Convert number to scaled bigint
			// Use string conversion to avoid floating point precision issues
			this.value = WEI.parseDecimalNumber(input.toString(), this.precision);
		} else {
			throw new Error(`Invalid input type for WEI: ${typeof input}`);
		}
	}

	/**
	 * Creates a new WEI instance asynchronously (factory method)
	 *
	 * @param {bigint | number | string} input - The initial value
	 *   - bigint/string: Raw scaled WEI value
	 *   - number: Decimal value to be scaled
	 * @param {number | IWEIAsset} config - Optional precision or asset config
	 * @returns {Promise<WEI>} A promise that resolves to a WEI instance
	 * @throws {Error} If the input cannot be converted to a valid WEI or if string contains decimals
	 *
	 * @example
	 * const bn = await WEI.from(2.5);              // Number: decimal value
	 * const usdc = await WEI.from("100000000", 6); // String: raw WEI value for 100 USDC
	 */
	public static async from(input: bigint | number | string, config?: number | IWEIAsset): Promise<WEI> {
		return new WEI(input, config);
	}

	/**
	 * Creates a new WEI instance synchronously (factory method)
	 *
	 * @param {bigint | number | string} input - The initial value
	 *   - bigint/string: Raw scaled WEI value
	 *   - number: Decimal value to be scaled
	 * @param {number | IWEIAsset} config - Optional precision or asset config
	 * @returns {WEI} A WEI instance
	 * @throws {Error} If the input cannot be converted to a valid WEI or if string contains decimals
	 *
	 * @example
	 * const bn = WEI.fromSync(2.5);              // Number: decimal value
	 * const usdc = WEI.fromSync("100000000", 6); // String: raw WEI value for 100 USDC
	 */
	public static fromSync(input: bigint | number | string, config?: number | IWEIAsset): WEI {
		return new WEI(input, config);
	}


	public static fromStorage(input: bigint | number | string, config?: number | IWEIAsset): WEI {
		return new WEI(input, config);
	}

	/**
	 * Parse a raw WEI string (integer only, no decimals)
	 *
	 * @param {string} input - Integer string representing raw WEI value (e.g., "2500000", "-1500000")
	 * @returns {bigint} The bigint value
	 * @throws {Error} If the string contains decimals or is invalid
	 */
	private static parseWEIString(input: string): bigint {
		const trimmed = input.trim();

		// Validate format: must be integer string only (no decimals allowed)
		if (!/^-?[0-9]+$/.test(trimmed)) {
			if (/\./.test(trimmed)) {
				throw new Error(`WEI strings must be raw scaled values (integers only). Got: ${input}. Use a number instead for decimal values.`);
			}
			throw new Error(`Invalid WEI string format: ${input}`);
		}

		return BigInt(trimmed);
	}

	/**
	 * Parse a decimal number string and convert to a scaled bigint
	 *
	 * @param {string} input - Decimal string from number.toString() (e.g., "2.5", "123.456789")
	 * @param {number} precision - Number of decimal places
	 * @returns {bigint} Scaled bigint value
	 * @throws {Error} If the string is invalid
	 */
	private static parseDecimalNumber(input: string, precision: number): bigint {
		const trimmed = input.trim();

		// Handle negative sign
		const isNegative = trimmed.startsWith("-");
		const absolute = isNegative ? trimmed.slice(1) : trimmed;

		// Validate format
		if (!/^[0-9]+(\.[0-9]*)?$/.test(absolute) && !/^\.[0-9]+$/.test(absolute)) {
			throw new Error(`Invalid decimal number format: ${input}`);
		}

		// Split into integer and fractional parts
		const parts = absolute.split(".");
		const integerPart = parts[0] || "0";
		const fractionalPart = parts[1] || "";

		// Pad or truncate fractional part to precision digits
		let scaledFractional = fractionalPart;
		if (scaledFractional.length < precision) {
			// Pad with zeros
			scaledFractional = scaledFractional.padEnd(precision, "0");
		} else if (scaledFractional.length > precision) {
			// Truncate (could also round, but truncation is simpler and more predictable)
			scaledFractional = scaledFractional.slice(0, precision);
		}

		// Combine integer and fractional parts
		const combined = integerPart + scaledFractional;
		const value = BigInt(combined);

		return isNegative ? -value : value;
	}

	/**
	 * Get the precision (number of decimal places) for this WEI instance
	 *
	 * @returns {number} The precision value (e.g., 18 for ETH, 6 for USDC)
	 *
	 * @example
	 * const eth = new WEI(1);               // eth.getPrecision() returns 18
	 * const usdc = new WEI(1, 6);           // usdc.getPrecision() returns 6
	 * const btc = new WEI(1, { decimals: 8 }); // btc.getPrecision() returns 8
	 */
	getPrecision(): number {
		return this.precision;
	}

	/**
	 * Get the underlying raw bigint value (scaled by 10^precision)
	 *
	 * @returns {bigint} The stored bigint value (e.g., 2500000000000000000n for 2.5)
	 */
	toBigInt(): bigint {
		return this.value;
	}

	/**
	 * Convert to raw WEI string representation
	 *
	 * @returns {string} String representation of the raw WEI value (e.g., "2500000000000000000")
	 *
	 * @example
	 * new WEI(2.5).toString()  // "2500000000000000000"
	 * new WEI(100, 6).toString()  // "100000000"
	 */
	toString(): string {
		return this.value.toString();
	}

	/**
	 * Convert to decimal string representation
	 *
	 * @param {number} maxDecimals - Maximum number of decimal places to show (default: precision)
	 * @returns {string} String representation of the decimal value (e.g., "2.5")
	 *
	 * @example
	 * new WEI(2.5).toDecimalString()  // "2.5"
	 * new WEI(100.123456, 6).toDecimalString(2)  // "100.12"
	 */
	toDecimalString(maxDecimals?: number): string {
		const maxDec = maxDecimals ?? this.precision;
		const isNegative = this.value < 0n;
		const absolute = isNegative ? -this.value : this.value;

		// Handle precision 0 case (no decimals)
		if (this.precision === 0) {
			return isNegative ? `-${absolute.toString()}` : absolute.toString();
		}

		// Convert to string and pad with leading zeros if necessary
		const valueStr = absolute.toString().padStart(this.precision + 1, "0");

		// Split into integer and fractional parts
		const integerPart = valueStr.slice(0, -this.precision) || "0";
		let fractionalPart = valueStr.slice(-this.precision);

		// Trim trailing zeros and apply maxDec
		fractionalPart = fractionalPart.slice(0, maxDec).replace(/0+$/, "");

		// Build result
		const result = fractionalPart.length > 0 ? `${integerPart}.${fractionalPart}` : integerPart;

		return isNegative ? `-${result}` : result;
	}

	/**
	 * Format the value as a string with specified decimal precision and optional prefix/suffix
	 * 
	 * Note: Unlike toDecimalString(), this method pads with trailing zeros to match the exact precision
	 *
	 * @param {object} options - Formatting options
	 * @param {number} options.precision - Number of decimal places to show (will pad with zeros)
	 * @param {string} options.prefix - Optional string to prepend to the result
	 * @param {string} options.suffix - Optional string to append to the result
	 * @param {boolean} options.percent - When true, multiply value by 100 (e.g., 0.12 becomes 12)
	 * @returns {string} Formatted string representation with exact precision (e.g., "2.50", "$2.50", "12.00%")
	 *
	 * @example
	 * const wei = new WEI(2.5);
	 * wei.formatted({ precision: 2 })  // "2.50"
	 * wei.formatted({ precision: 2, prefix: "$" })  // "$2.50"
	 * wei.formatted({ precision: 2, suffix: " ETH" })  // "2.50 ETH"
	 * wei.formatted({ precision: 2, prefix: "$", suffix: " USD" })  // "$2.50 USD"
	 * 
	 * const rate = new WEI(0.12);
	 * rate.formatted({ precision: 2, percent: true })  // "12.00"
	 * rate.formatted({ precision: 2, percent: true, suffix: "%" })  // "12.00%"
	 */
	formatted({ type, precision, prefix, suffix, percent, }: { type?: "CURRENCY" | "DOLLARS"; precision: number; prefix?: string; suffix?: string; percent?: boolean; }): string {
		let baseValue: WEI;
		if(percent) {
			// Multiply by 100 for percentage - create new WEI to maintain precision
			baseValue = this.multiply(100);
		} else {
			baseValue = this;
		}

		const isNegative = baseValue.value < 0n;
		const absolute = isNegative ? -baseValue.value : baseValue.value;

		if(type && type === "CURRENCY") {
			const isUSD = ["USD", "USDC", "USDT"].includes(this.asset?.symbol ?? "");
			const precision = typeof this.asset?.precision === "number" ? this.asset.precision : isUSD ? 2 : 5;
			return `${this.toDecimalString(precision)} ${this.asset?.label ?? this.asset?.symbol ?? ""}`;
		}

		// Handle precision 0 case (no decimals)
		if(precision === 0) {
			const integerValue = absolute / baseValue.precisionMultiplier;
			const value = isNegative ? `-${integerValue.toString()}` : integerValue.toString();
			return `${prefix ?? ""}${value}${suffix ? (typeof suffix === "string" ? suffix : suffix === "LABEL" ? this.asset?.label : this.asset?.symbol) : ""}`;
		}

		// Convert to string and pad with leading zeros if necessary
		const valueStr = absolute.toString().padStart(baseValue.precision + 1, "0");

		// Split into integer and fractional parts
		const integerPart = valueStr.slice(0, -baseValue.precision) || "0";
		let fractionalPart = valueStr.slice(-baseValue.precision);

		// Truncate or pad to exact precision
		fractionalPart = fractionalPart.slice(0, precision).padEnd(precision, "0");

		// Build result
		const value = `${integerPart}.${fractionalPart}`;
		const finalValue = isNegative ? `-${value}` : value;

		return `${prefix ?? ""}${finalValue}${suffix ?? ""}`;
	}

	/**
	 * Convert to number (may lose precision for large values)
	 *
	 * @returns {number} Number representation (warning: may lose precision)
	 */
	toNumber(): number {
		// Divide by the precision multiplier to get the decimal value
		const integerPart = this.value / this.precisionMultiplier;
		const fractionalPart = this.value % this.precisionMultiplier;

		return Number(integerPart) + Number(fractionalPart) / Number(this.precisionMultiplier);
	}

	/**
	 * Add another WEI or value
	 *
	 * @param {WEI | bigint | number | string} other - Value to add
	 * @returns {WEI} New WEI with the sum
	 *
	 * @example
	 * new WEI(2.5).add(1.5)  // 4.0
	 * new WEI(1).add("0.5")  // 1.5
	 */
	add(other: WEI | bigint | number | string): WEI {
		const otherBN = other instanceof WEI ? other : new WEI(other, this.precision);
		return new WEI(this.value + otherBN.value, this.asset ?? this.precision);
	}

	/**
	 * Subtract another WEI or value
	 *
	 * @param {WEI | bigint | number | string} other - Value to subtract
	 * @returns {WEI} New WEI with the difference
	 *
	 * @example
	 * new WEI(5).subtract(2.5)    // 2.5
	 * new WEI(3.5).subtract("1")  // 2.5
	 */
	subtract(other: WEI | bigint | number | string): WEI {
		const otherBN = other instanceof WEI ? other : new WEI(other, this.precision);
		return new WEI(this.value - otherBN.value, this.asset ?? this.precision);
	}

	/**
	 * Multiply by another WEI or value
	 *
	 * @param {WEI | bigint | number | string} other - Value to multiply by
	 * @returns {WEI} New WEI with the product
	 *
	 * @example
	 * new WEI(2.5).multiply(2)      // 5.0
	 * new WEI(1.5).multiply("2.5")  // 3.75
	 */
	multiply(other: WEI | bigint | number | string): WEI {
		const otherBN = other instanceof WEI ? other : new WEI(other, this.precision);
		// When multiplying two scaled values, we get a value scaled by precision^2
		// So we need to divide by precision to get back to precision scale
		const result = (this.value * otherBN.value) / this.precisionMultiplier;
		return new WEI(result, this.asset ?? this.precision);
	}

	/**
	 * Divide by another WEI or value
	 *
	 * @param {WEI | bigint | number | string} other - Value to divide by
	 * @returns {WEI} New WEI with the quotient
	 * @throws {Error} If dividing by zero
	 *
	 * @example
	 * new WEI(5).divide(2)        // 2.5
	 * new WEI(7.5).divide("2.5")  // 3.0
	 */
	divide(other: WEI | bigint | number | string): WEI {
		const otherBN = other instanceof WEI ? other : new WEI(other, this.precision);
		if(otherBN.value === 0n) {
			throw new Error("Cannot divide by zero");
		}
		// When dividing two scaled values, we need to multiply the numerator by precision
		// to maintain the precision scale: (a * 10^p) / (b * 10^p) * 10^p = (a * 10^p) / b
		const result = (this.value * this.precisionMultiplier) / otherBN.value;
		return new WEI(result, this.asset ?? this.precision);
	}

	/**
	 * Get the modulo (remainder) of division
	 *
	 * @param {WEI | bigint | number | string} other - Divisor
	 * @returns {WEI} New WEI with the remainder
	 *
	 * @example
	 * new WEI(5.5).mod(2)  // 1.5
	 */
	mod(other: WEI | bigint | number | string): WEI {
		const otherBN = other instanceof WEI ? other : new WEI(other, this.precision);
		return new WEI(this.value % otherBN.value, this.asset ?? this.precision);
	}

	/**
	 * Raise to a power
	 *
	 * @param {number | bigint} exponent - The exponent (must be non-negative integer)
	 * @returns {WEI} New WEI with the result
	 *
	 * @example
	 * new WEI(2).pow(3)    // 8.0
	 * new WEI(1.5).pow(2)  // 2.25
	 */
	pow(exponent: number | bigint): WEI {
		const exp = WEI.fromSync(exponent, this.asset ?? this.precision);
		if(exp.value < 0n) {
			throw new Error("Exponent must be non-negative");
		}
		if(exp.value === 0n) {
			return new WEI(1, this.asset ?? this.precision);
		}
		const baseDecimal = new Decimal(this.value.toString()).div(Decimal.pow(10, this.precision));
		const exponentDecimal = new Decimal(exp.value.toString()).div(Decimal.pow(10, this.precision));
		const result = baseDecimal.pow(exponentDecimal).mul(Decimal.pow(10, this.precision));
		return WEI.fromSync(result.floor().toString(), this.asset ?? this.precision);
	}

	/**
	 * Get the absolute value
	 *
	 * @returns {WEI} New WEI with absolute value
	 */
	abs(): WEI {
		return new WEI(this.value < 0n ? -this.value : this.value, this.asset ?? this.precision);
	}

	/**
	 * Check if equal to another value
	 *
	 * @param {WEI | bigint | number | string} other - Value to compare
	 * @returns {boolean} True if equal
	 */
	equals(other: WEI | bigint | number | string): boolean {
		const otherBN = other instanceof WEI ? other : new WEI(other, this.precision);
		return this.value === otherBN.value;
	}

	/**
	 * Check if less than another value
	 *
	 * @param {WEI | bigint | number | string} other - Value to compare
	 * @returns {boolean} True if this is less than other
	 */
	lessThan(other: WEI | bigint | number | string): boolean {
		const otherBN = other instanceof WEI ? other : new WEI(other, this.precision);
		return this.value < otherBN.value;
	}

	/**
	 * Check if less than or equal to another value
	 *
	 * @param {WEI | bigint | number | string} other - Value to compare
	 * @returns {boolean} True if this is less than or equal to other
	 */
	lessThanOrEqual(other: WEI | bigint | number | string): boolean {
		const otherBN = other instanceof WEI ? other : new WEI(other, this.precision);
		return this.value <= otherBN.value;
	}

	/**
	 * Check if greater than another value
	 *
	 * @param {WEI | bigint | number | string} other - Value to compare
	 * @returns {boolean} True if this is greater than other
	 */
	greaterThan(other: WEI | bigint | number | string): boolean {
		const otherBN = other instanceof WEI ? other : new WEI(other, this.precision);
		return this.value > otherBN.value;
	}

	/**
	 * Check if greater than or equal to another value
	 *
	 * @param {WEI | bigint | number | string} other - Value to compare
	 * @returns {boolean} True if this is greater than or equal to other
	 */
	greaterThanOrEqual(other: WEI | bigint | number | string): boolean {
		const otherBN = other instanceof WEI ? other : new WEI(other, this.precision);
		return this.value >= otherBN.value;
	}

	/**
	 * Check if the value is zero
	 *
	 * @returns {boolean} True if zero
	 */
	isZero(): boolean {
		return this.value === 0n;
	}

	/**
	 * Check if the value is negative
	 *
	 * @returns {boolean} True if negative
	 */
	isNegative(): boolean {
		return this.value < 0n;
	}

	/**
	 * Check if the value is positive
	 *
	 * @returns {boolean} True if positive (greater than zero)
	 */
	isPositive(): boolean {
		return this.value > 0n;
	}

	public toJSON(): string {
		const { asset, precision } = this;
		if(asset) {
			const { decimals, symbol, chainId } = asset;
			return JSON.stringify([this.value.toString(), [decimals, symbol, chainId, ]]);
		}
		return JSON.stringify([this.value.toString(), precision]);
	}

	public static fromJSON(input: string): WEI {
		const [value, assetOrPrecision] = JSON.parse(input);
		if(typeof assetOrPrecision === "number") {
			return WEI.fromSync(BigInt(value), assetOrPrecision);
		}
		if(Array.isArray(assetOrPrecision)) {
			const [decimals, symbol, chainId] = assetOrPrecision;
			const asset = getAsset(symbol, chainId);
			if(asset) {
				return WEI.fromSync(BigInt(value), asset);
			}
			return WEI.fromSync(BigInt(value), { decimals, symbol, chainId, });
		}
		throw new Error("invalid asset or precision");
	}

	public serialize() {
		return this.toJSON();
	}

	public static deserialize(input: string): WEI {
		return WEI.fromJSON(input);
	}

	public storable() {
		return this.toJSON();
	}

	public fromStorage(input: string): WEI {
		return WEI.fromJSON(input);
	}

}
